import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Layout,
  Card,
  Table,
  Button,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  Statistic,
  Input,
  Select,
  DatePicker,
  Avatar,
  Tooltip,
  Empty,
} from "antd";
import {
  FaSearch,
  FaFilter,
  FaCalendarAlt,
  FaUsers,
  FaFileExcel,
  FaClipboardList,
  FaBuilding,
  FaUserTag,
  FaProjectDiagram,
  FaClock,
  FaCheckCircle,
} from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Api from "../api/Api";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import "./Projeto.css";

const { Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

/* ─────────────────────────────────────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────────────────────────────────────── */
const formatDateTime = (str) => {
  if (!str) return "—";
  try {
    return dayjs(str).format("DD/MM/YYYY HH:mm");
  } catch {
    return str;
  }
};

const formatDuration = (inicio, fim) => {
  if (!inicio || !fim) return "—";
  const dI = dayjs(inicio);
  const dF = dayjs(fim);
  const totalMin = dF.diff(dI, "minute");
  if (totalMin < 0) return "—";
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
};

const getInitials = (nome) => {
  if (!nome) return "?";
  const partes = nome.trim().split(" ");
  if (partes.length === 1) return partes[0].charAt(0).toUpperCase();
  return (partes[0].charAt(0) + partes[partes.length - 1].charAt(0)).toUpperCase();
};

// Gera uma cor consistente a partir do nome (para avatares)
const getColorFromName = (nome) => {
  const colors = ["#1677ff", "#52c41a", "#fa8c16", "#722ed1", "#13c2c2", "#eb2f96", "#2f54eb"];
  if (!nome) return colors[0];
  let hash = 0;
  for (let i = 0; i < nome.length; i++) hash = nome.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
};

/* ─────────────────────────────────────────────────────────────────────────────
   RELATORIO SEMANAL PAGE
───────────────────────────────────────────────────────────────────────────── */
const RelatorioSemanal = () => {
  const [registros, setRegistros] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [userFilter, setUserFilter] = useState("TODOS");
  const [direcaoFilter, setDirecaoFilter] = useState("TODAS");
  const [periodo, setPeriodo] = useState([
    dayjs().startOf("month"),
    dayjs(),
  ]);

  useEffect(() => {
    document.title = "Relatório Semanal | Dashboard";
  }, []);

  const loadRelatorio = useCallback(async (range) => {
    if (!range || range.length !== 2) return;
    setLoading(true);
    try {
          const userStored = JSON.parse(localStorage.getItem("user"));
      const payload = {
        dataInicio: range[0].format("YYYY-MM-DD"),
        dataFim: range[1].format("YYYY-MM-DD"),
        perfil: userStored.perfil,
        direcao: userStored.direcao,
      };
      const response = await Api.ListRelatorioSemanal(payload);
      setRegistros(Array.isArray(response) ? response : []);
    } catch {
      toast.error("Erro ao carregar o relatório.");
      setRegistros([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRelatorio(periodo);
  }, [periodo, loadRelatorio]);

  /* ── Listas únicas para os filtros, derivadas dos próprios dados ── */
  const usuariosUnicos = useMemo(() => {
    const set = new Set(registros.map((r) => r.nome).filter(Boolean));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [registros]);

  const direcoesUnicas = useMemo(() => {
    const set = new Set(registros.map((r) => r.direcao).filter(Boolean));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [registros]);

  /* ── Filtragem ── */
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return registros.filter((r) => {
      const matchUser = userFilter === "TODOS" || r.nome === userFilter;
      const matchDirecao = direcaoFilter === "TODAS" || r.direcao === direcaoFilter;

      const matchSearch =
        !q ||
        r.nome?.toLowerCase().includes(q) ||
        r.titulo?.toLowerCase().includes(q) ||
        r.projeto?.toLowerCase().includes(q);

      return matchUser && matchDirecao && matchSearch;
    });
  }, [registros, search, userFilter, direcaoFilter]);

  /* ── Estatísticas ── */
  const totalAtividades = filtered.length;
  const totalColaboradores = useMemo(
    () => new Set(filtered.map((r) => r.nome)).size,
    [filtered]
  );
  const totalComProjeto = filtered.filter((r) => r.projeto).length;
  const totalMinutos = useMemo(
    () =>
      filtered.reduce((acc, r) => {
        if (!r.inicio || !r.fim) return acc;
        const diff = dayjs(r.fim).diff(dayjs(r.inicio), "minute");
        return acc + (diff > 0 ? diff : 0);
      }, 0),
    [filtered]
  );
  const horasFormatadas = `${Math.floor(totalMinutos / 60)}h ${totalMinutos % 60}m`;

  /* ── Exportar para Excel ── */
  const exportToExcel = () => {
    if (filtered.length === 0) {
      toast.warning("Não há dados para exportar.");
      return;
    }

    const dataExport = filtered.map((r) => ({
      Colaborador: r.nome || "—",
      Direção: r.direcao || "—",
      Título: r.titulo   ? r.titulo : "—",
      Descrição: r.descricao  ? r.descricao : "—",
      Projeto: r.projeto || "—",
      Início: formatDateTime(r.inicio),
      Fim: formatDateTime(r.fim),
      Duração: formatDuration(r.inicio, r.fim),
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataExport);

    // Largura das colunas
    worksheet["!cols"] = [
      { wch: 24 }, // Colaborador
      { wch: 32 }, // Direção
      { wch: 30 }, // Título
      { wch: 45 }, // Descrição
      { wch: 24 }, // Projeto
      { wch: 18 }, // Início
      { wch: 18 }, // Fim
      { wch: 12 }, // Duração
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Relatório Semanal");

    const nomeArquivo = `relatorio_semanal_${periodo[0].format("YYYYMMDD")}_a_${periodo[1].format("YYYYMMDD")}.xlsx`;
    XLSX.writeFile(workbook, nomeArquivo);

    toast.success("Relatório exportado com sucesso!");
  };

  /* ── Colunas da tabela ── */
  const columns = [
    {
      title: "Colaborador",
      dataIndex: "nome",
      key: "nome",
      width: 220,
      render: (nome, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Avatar
            size="default"
            style={{
              backgroundColor: getColorFromName(nome),
              fontWeight: 700,
              fontSize: 12,
              flexShrink: 0,
            }}
          >
            {getInitials(nome)}
          </Avatar>
          <div>
            <div style={{ fontWeight: 600, color: "#1f1f1f" }}>{nome || "—"}</div>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {record.direcao || "—"}
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: "Atividade",
      key: "atividade",
      width: 320,
      render: (_, record) => {
        const titulo = record.titulo ? record.titulo : null;
        const descricao = record.descricao  ? record.descricao : null;
        return (
          <div>
            <div style={{ fontWeight: 600, color: "#1f1f1f", marginBottom: 2 }}>
              {titulo || <Text type="secondary" italic>Sem título</Text>}
            </div>
            {descricao && (
              <Text
                type="secondary"
                style={{
                  fontSize: 12,
                  display: "block",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: 300,
                }}
                title={descricao}
              >
                {descricao}
              </Text>
            )}
          </div>
        );
      },
    },
    {
      title: "Projeto",
      dataIndex: "projeto",
      key: "projeto",
      width: 180,
      render: (projeto) =>
        projeto ? (
<Tag
  color="blue"
  icon={<FaProjectDiagram style={{ marginRight: 4 }} />}
  style={{
    borderRadius: 20,
    padding: "4px 12px",
    fontWeight: 500,
    whiteSpace: "normal",
    wordBreak: "break-word",
    maxWidth: 150,
    height: "auto",
    lineHeight: "18px",
  }}
>
  {projeto}
</Tag>
        ) : (
          <Text type="secondary" style={{ fontSize: 12 }}>
            Sem projeto
          </Text>
        ),
    },
    {
      title: "Início",
      dataIndex: "inicio",
      key: "inicio",
      width: 160,
      render: (inicio) => (
        <Text style={{ fontSize: 13 }}>
          <FaCalendarAlt style={{ marginRight: 6, color: "#8c8c8c" }} />
          {formatDateTime(inicio)}
        </Text>
      ),
    },
    {
      title: "Fim",
      dataIndex: "fim",
      key: "fim",
      width: 160,
      render: (fim) => (
        <Text style={{ fontSize: 13 }}>
          <FaCalendarAlt style={{ marginRight: 6, color: "#8c8c8c" }} />
          {formatDateTime(fim)}
        </Text>
      ),
    },
    {
      title: "Duração",
      key: "duracao",
      width: 110,
      render: (_, record) => (
        <Tag
          style={{
            borderRadius: 20,
            padding: "4px 10px",
            fontWeight: 600,
            background: "#f0f5ff",
            color: "#1677ff",
            border: "1px solid #adc6ff",
          }}
          icon={<FaClock style={{ marginRight: 4 }} />}
        >
          {formatDuration(record.inicio, record.fim)}
        </Tag>
      ),
    },
  ];

  return (
    <Layout className="projeto-layout" style={{ background: "transparent" }}>
      <Content style={{ padding: "24px 32px" }}>
        <ToastContainer position="top-right" autoClose={4000} />

        {/* Cabeçalho */}
        <div
          style={{
            background: "linear-gradient(135deg, #1677ff 0%, #4096ff 100%)",
            borderRadius: 16,
            padding: "24px 32px",
            marginBottom: 32,
            boxShadow: "0 8px 20px rgba(22, 119, 255, 0.15)",
          }}
        >
          <Row align="middle" justify="space-between">
            <Col>
              <Space size="middle">
                <div
                  style={{
                    backgroundColor: "rgba(255,255,255,0.2)",
                    borderRadius: 12,
                    padding: 10,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FaClipboardList style={{ fontSize: 32, color: "#fff" }} />
                </div>
                <div>
                  <Title level={2} style={{ color: "#fff", margin: 0, fontWeight: 700 }}>
                    Relatório Semanal
                  </Title>
                  <Text style={{ color: "rgba(255,255,255,0.85)", fontSize: 13 }}>
                    Acompanhe as atividades dos colaboradores por período
                  </Text>
                </div>
              </Space>
            </Col>
            <Col>
              <Button
                type="primary"
                size="large"
                icon={<FaFileExcel />}
                onClick={exportToExcel}
                style={{
                  backgroundColor: "#fff",
                  color: "#1677ff",
                  border: "none",
                  fontWeight: 600,
                  borderRadius: 30,
                  padding: "0 24px",
                  height: 48,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                }}
              >
                Exportar Excel
              </Button>
            </Col>
          </Row>
        </div>

        {/* Cards de estatísticas */}
        <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
          {[
            {
              title: "Total de Atividades",
              value: totalAtividades,
              color: "#1677ff",
              bgColor: "#e6f4ff",
              icon: <FaClipboardList style={{ fontSize: 24, color: "#1677ff" }} />,
            },
            {
              title: "Colaboradores",
              value: totalColaboradores,
              color: "#52c41a",
              bgColor: "#f6ffed",
              icon: <FaUsers style={{ fontSize: 24, color: "#52c41a" }} />,
            },
            {
              title: "Com Projeto Associado",
              value: totalComProjeto,
              color: "#722ed1",
              bgColor: "#f9f0ff",
              icon: <FaProjectDiagram style={{ fontSize: 24, color: "#722ed1" }} />,
            },
            {
              title: "Tempo Total Registado",
              value: horasFormatadas,
              color: "#fa8c16",
              bgColor: "#fff7e6",
              icon: <FaClock style={{ fontSize: 24, color: "#fa8c16" }} />,
              isText: true,
            },
          ].map((stat, i) => (
            <Col xs={24} sm={12} lg={6} key={i}>
              <Card
                className="stat-card"
                bordered={false}
                style={{
                  borderRadius: 16,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                  border: "1px solid #f0f0f0",
                }}
                bodyStyle={{ padding: "20px 24px" }}
              >
                <Statistic
                  title={<span style={{ fontSize: 14, color: "#8c8c8c" }}>{stat.title}</span>}
                  value={stat.value}
                  valueStyle={{
                    fontSize: stat.isText ? 24 : 32,
                    fontWeight: 700,
                    color: stat.color,
                  }}
                  prefix={
                    <div
                      style={{
                        backgroundColor: stat.bgColor,
                        borderRadius: 12,
                        padding: 10,
                        marginRight: 16,
                      }}
                    >
                      {stat.icon}
                    </div>
                  }
                />
              </Card>
            </Col>
          ))}
        </Row>

        {/* Barra de ferramentas / filtros */}
        <Card
          bordered={false}
          style={{
            borderRadius: 16,
            marginBottom: 24,
            boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
          }}
          bodyStyle={{ padding: "16px 24px" }}
        >
          <Row gutter={[16, 16]} align="middle">
            <Col flex="auto" style={{ minWidth: 260 }}>
              <Input
                size="large"
                prefix={<FaSearch style={{ color: "#bfbfbf" }} />}
                placeholder="Pesquisar por nome, título ou projeto..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                allowClear
                style={{ borderRadius: 30 }}
              />
            </Col>

            <Col>
              <RangePicker
                size="large"
                format="DD/MM/YYYY"
                value={periodo}
                onChange={(dates) => {
                  if (dates && dates[0] && dates[1]) {
                    setPeriodo(dates);
                  }
                }}
                allowClear={false}
                style={{ borderRadius: 30 }}
              />
            </Col>

            <Col>
              <Select
                size="large"
                style={{ width: 200, borderRadius: 30 }}
                value={userFilter}
                onChange={setUserFilter}
                showSearch
                optionFilterProp="children"
                suffixIcon={<FaUserTag style={{ color: "#1677ff" }} />}
              >
                <Option value="TODOS">Todos os colaboradores</Option>
                {usuariosUnicos.map((nome) => (
                  <Option key={nome} value={nome}>
                    {nome}
                  </Option>
                ))}
              </Select>
            </Col>

            <Col>
              <Select
                size="large"
                style={{ width: 220, borderRadius: 30 }}
                value={direcaoFilter}
                onChange={setDirecaoFilter}
                showSearch
                optionFilterProp="children"
                suffixIcon={<FaBuilding style={{ color: "#1677ff" }} />}
              >
                <Option value="TODAS">Todas as direções</Option>
                {direcoesUnicas.map((direcao) => (
                  <Option key={direcao} value={direcao}>
                    {direcao}
                  </Option>
                ))}
              </Select>
            </Col>
          </Row>
        </Card>

        {/* Tabela de atividades */}
        <Card
          bordered={false}
          style={{
            borderRadius: 16,
            boxShadow: "0 4px 16px rgba(0,0,0,0.05)",
            overflow: "hidden",
          }}
          bodyStyle={{ padding: 0 }}
        >
          <Table
            columns={columns}
            dataSource={filtered}
            rowKey={(record, index) => `${record.nome}-${record.inicio}-${index}`}
            loading={loading}
            pagination={{
              pageSize: 15,
              showSizeChanger: true,
              showTotal: (total) => `Total de ${total} atividades`,
              style: { padding: "16px 24px" },
            }}
            locale={{
              emptyText: (
                <Empty
                  description="Nenhuma atividade encontrada para os filtros selecionados"
                  style={{ padding: "40px 0" }}
                />
              ),
            }}
            scroll={{ x: 1300 }}
          />
        </Card>
      </Content>
    </Layout>
  );
};

export default RelatorioSemanal;